import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React from "react";
import downloadFile from "_shared/_utilities/download-file";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import DocumentListColumn from "work/work-agreements/contracts/_views/document-list-column";
import { documentDownloadFailureMessage } from "documents/_api/document-download-failure";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import { Masked } from "work/masked-fields";
import WorkAgreement from "work/work-agreements/work-agreement";
import { workPaneNarrowerThan, workPaneProseInset } from "work/_views/work-pane-layout";

/**
 * No left margin. The list's heading and rows carry Material's 16px inset of their own, which is the
 * prose inset already, so a margin here stood the heading 40px right of the Leave Team button above
 * it. The helper text gets the inset it has no list to give it.
 */
const ListContainer = styled(Grid)(({ theme }) => ({
  flexWrap: "nowrap",
  height: "100%",
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  overflowY: "auto",
}));
const HelperText = styled(Typography)(({ theme }) => ({
  ...workPaneProseInset(theme),
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type ConflictsTabProps = {
  workAgreement: WorkAgreement;
};

export default function ConflictsTab(props: Readonly<ConflictsTabProps>) {
  const { workAgreement } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const [downloadDocument] = useLazyDownloadDocumentQuery();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const conflictsDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
      await downloadFile(conflictsDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(documentDownloadFailureMessage(err, "Failed to download document. Please try again later."), { variant: "error" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (!workAgreement) return null;

  if (Masked.isMasked(workAgreement.conflictsCheckWaived) || Masked.isMasked(workAgreement.conflicts)) {
    return "You do not have permission to view contracts.";
  }

  return (
    <ListContainer container direction="row">
      <DocumentListColumn>
        {workAgreement?.conflictsCheckWaived.value ? (
          <HelperText>Conflicts check waived</HelperText>
        ) : (
          <>
            {workAgreement.conflicts.length === 0 && (
              <HelperText>No conflicts documents</HelperText>
            )}
            {workAgreement.conflicts.length > 0 && (
              <DocumentList
                documents={workAgreement.conflicts}
                documentType={WorkDocumentType.Conflicts}
                downloadingFileId={isDownloadingFile}
                onDownload={handleDownloadDocumentById}
              />
            )}
          </>
        )}
      </DocumentListColumn>
    </ListContainer>
  );
}
